
import { 
    AppEnum,
    FaCalendar, 
    FaDollarSign, 
    FaHashtag, 
    FaUserTag, 
    Header, 
    NeedEnums,
    OptionHelper,
    RecordMap
} from "@/Shared";

import { CityModel, SectorModel } from '@/Domains';

import { NeedViewModel } from "../Logic/ViewModels";

export type SelectOption = {
    value: any,
    label: any
}


/**
 * =====================================================
 * NeedHeader
 * =====================================================
 *
 * Responsible for defining column headers for the Need table or grid:
 * - Provides metadata for rendering (icon, label, type)
 * - Configures sortable and filterable properties
 * - Maps enums and domain lists to select/filter options
 * 
 * Note: This class is designed as a static builder; the constructor is private.
 *
 * Author: Samano CASTRE
* Date: July 17, 2025
 */
export class NeedHeader {


    /** Private constructor to enforce static-like usage */
    private constructor() { }

    get id() {
        return {
            key: 'id',
            icon: <FaHashtag />,
            label: 'ID',
            type: 'number',
            isSortable: true
        }
    }

    getOperationType(oprationTypes: AppEnum[]) {
        return {
            key: 'operationType',
            icon: <FaUserTag />,
            label: 'Type d\'opération',
            type: 'string',
            isSortable: true,
            filter: {
                label: "Opération",
                options: oprationTypes,
                case: 'sensitive'
            }
        }
    }

    getType(types: AppEnum[]) {
        return {
            key: 'propertyType',
            icon: <FaUserTag />,
            label: 'Type de bien',
            type: 'string',
            isSortable: true,
            filter: {
                label: "Type",
                options: types,
                case: 'sensitive'
            }
        }
    }

    getCategory(categoryTypes: AppEnum[]) {
        return {
            key: 'propertyCategory',
            icon: <FaUserTag />,
            label: 'Catégorie de bien',
            type: 'string',
            isSortable: true,
            isHidden: true,
            filter: {
                label: "Catégorie",
                options: categoryTypes,
                case: 'sensitive'
            }
        }
    }


    getSector(sectorMap: RecordMap<SectorModel>) {
        return {
            key: 'sectorName',
            icon: <FaUserTag />,
            label: 'Secteur',
            type: 'string',
            isSortable: true,
            filter: {
                label: "Localisation",
                options: OptionHelper.sectorOptions(sectorMap.toArray()),
                case: 'sensitive'
            }
        }
    }

    getPriceRange(priceRangeOptions : AppEnum[]) {
        
        return {
            key: 'priceRangeLabel',
            icon: <FaDollarSign />,
            label: 'Budget',
            type: 'number',
            isSortable: true,
            filter: {
                label: "Budget",
                options: OptionHelper.sortByLabel(priceRangeOptions),
                case: 'sensitive'
            }
        }
    }

    getCity(cityMap: RecordMap<CityModel>) {

        return {
            key: 'cityName',
            icon: <FaUserTag />,
            label: 'Ville',
            type: 'string',
            isSortable: true,
            /*
            filter: {
                label: "Ville",
                options: cityEnums,
                case: 'sensitive'
            }*/
        }
    }


    get createdAt() {
        return {
            key: 'createdAt',
            icon: <FaCalendar />,
            label: 'Date',
            type: 'string',
            isSortable: true
        }
    }

    getStatus(statuses: AppEnum[]) {
        return {
            key: 'status',
            icon: <FaUserTag />,
            label: 'Résultat besoin',
            type: 'string',
            isSortable: true,
            filter: {
                label: "Selon Résultat",
                options: statuses,
                case: 'sensitive'
            }
        }
    }

    getContact() {
        return {
            key: 'contactName',
            icon: <FaUserTag />,
            label: 'Contact',
            type: 'string',
            isSortable: true,
        }
    }


    /**
     * =====================================================
     * Static builder
     * =====================================================
     *
     * Builds a full set of Need headers ready for table/grid display.
     * Maps enums and domain lists to filter options where applicable.
     *
     * @param sectorMap - Map of sectors
     * @param cityMap - Map of cities
     * @param priceRangeMap - Map of price ranges
     * @param viewEnums - All enums needed for display/filtering
     */
    static build(
        sectorMap: RecordMap<SectorModel>,
        cityMap: RecordMap<CityModel>,
        viewEnums: NeedEnums

    ): Record<keyof NeedViewModel, Header<NeedViewModel>> {

        const header = new NeedHeader();

        return {
            id: header.id,
            status: header.getStatus(viewEnums.NeedStatusEnum),
            createdAt: header.createdAt,
            cityName: header.getCity(cityMap),
            sectorName: header.getSector(sectorMap),
            propertyType: header.getType(viewEnums.PropertyTypeEnum),
            propertyCategory: header.getCategory(viewEnums.PropertyCategoryEnum),
            operationType: header.getOperationType(viewEnums.OperationTypeEnum),
            contactName: header.getContact(),
            priceRangeLabel: header.getPriceRange(viewEnums.PriceRangeEnum)
        } as Record<keyof NeedViewModel, Header<NeedViewModel>>
    }
}